import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { fetchRows, createRow, updateRow } from '../lib/api'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'

function getVal(v) {
  if (v === null || v === undefined) return ''
  if (typeof v === 'object' && 'value' in v) return v.value
  return v
}

function fmtDate(d) {
  if (!d) return ''
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const parts = String(d).split('-')
  if (parts.length !== 3) return d
  return `${parts[2]}-${months[parseInt(parts[1])-1]}-${parts[0]}`
}

import { latestReadings, planSync, EQUIP_DEFAULTS, EQUIP_UID, maintenanceRowsFor, serviceDue } from '../lib/engineHours'
import DateField from '../components/DateField'
import html2pdf from 'html2pdf.js'
import { escHtml } from '../lib/escHtml'

const EMPTY_READINGS = {
  tanks: { fuel_total: '', day_tank: '', port_aft: '', stbd_aft: '', port_mid: '', stbd_mid: '', port_fwd: '', stbd_fwd: '', black_grey: '', clean_lo: '', waste_lo: '', fresh_water: '' },
  confirm: { main_hyd: 'OK', passarelle_hyd: 'OK', steering_hyd: 'OK' },
  gen_port: { hours: '', coolant_temp: '', lo_pressure: '', lo_temp: '', power_kw: '', frequency: '', volts: '', rpm: '' },
  gen_stbd: { hours: '', coolant_temp: '', lo_pressure: '', lo_temp: '', power_kw: '', frequency: '', volts: '', rpm: '' },
  equipment: [
    { name: 'M.E Port', hours: '', next_service: '', oil_added: '' },
    { name: 'M.E Stbd', hours: '', next_service: '', oil_added: '' },
    { name: 'Gen Port', hours: '', next_service: '', oil_added: '' },
    { name: 'Gen Stbd', hours: '', next_service: '', oil_added: '' },
  ],
  shore_power: [
    { line: 1, voltage_p: '', voltage_s: '', current_p: '', current_s: '', load_kva_p: '', load_kva_s: '', convertor: '' },
    { line: 2, voltage_p: '', voltage_s: '', current_p: '', current_s: '', load_kva_p: '', load_kva_s: '', convertor: '' },
    { line: 3, voltage_p: '', voltage_s: '', current_p: '', current_s: '', load_kva_p: '', load_kva_s: '', convertor: '' },
  ],
  busbar: [
    { bus: 1, voltage: '', load: '', frequency: '', power_factor: '' },
    { bus: 2, voltage: '', load: '', frequency: '', power_factor: '' },
    { bus: 3, voltage: '', load: '', frequency: '', power_factor: '' },
  ],
  batteries: [
    { name: 'Port M.E Start', voltage: '', current: '' },
    { name: 'Stbd M.E Start', voltage: '', current: '' },
    { name: 'Ships Service', voltage: '', current: '' },
    { name: 'Emergency 24V', voltage: '', current: '' },
    { name: 'Emergency(2) 24V', voltage: '', current: '' },
  ],
  ro_plants: [
    { plant: 1, high_press: '', low_press: '', tds: '', prod: '' },
    { plant: 2, high_press: '', low_press: '', tds: '', prod: '' },
  ],
  ac: { mode: '', heat: '', cool: '', return_temp: '', supply_temp: '', cw_press: '' },
  misc: { compressed_air: 'OK', vacuum_system: 'OK', bilges_dry: 'OK', water_softener: 'OK', er_fans: 'OK' },
}

export default function EngineLog() {
  const { config } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [view, setView] = useState('table') // 'table' | 'form'
  const [editRow, setEditRow] = useState(null)
  const printRef = useRef()

  const [header, setHeader] = useState({ date: '', location: '', mode: '', signed_by: '', notes: '' })
  const [readings, setReadings] = useState(JSON.parse(JSON.stringify(EMPTY_READINGS)))
  const [hoursMsg, setHoursMsg] = useState(null)
  const [maintMsg, setMaintMsg] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try { const data = await fetchRows('engine_log'); setRows(data || []) }
    catch (e) { setError('Failed to load') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  function openNew() {
    const now = new Date()
    setHeader({ date: now.toISOString().split('T')[0], location: '', mode: '', signed_by: '', notes: '' })
    setReadings(JSON.parse(JSON.stringify(EMPTY_READINGS)))
    setEditRow(null)
    setView('form')
  }

  function openView(row) {
    setHeader({
      date: getVal(row['Date']), location: getVal(row['Location']),
      mode: getVal(row['Mode']), signed_by: getVal(row['Signed By']), notes: getVal(row['Notes'])
    })
    try {
      const r = JSON.parse(getVal(row['Readings']) || '{}')
      setReadings({ ...JSON.parse(JSON.stringify(EMPTY_READINGS)), ...r })
    } catch { setReadings(JSON.parse(JSON.stringify(EMPTY_READINGS))) }
    setEditRow(row)
    setView('form')
  }

  async function handleSave() {
    setSaving(true); setError('')
    try {
      const payload = {
        'Date': header.date, 'Location': header.location, 'Mode': header.mode,
        'Signed By': header.signed_by, 'Fuel Total': readings.tanks.fuel_total,
        'Day Tank': readings.tanks.day_tank, 'Fresh Water': readings.tanks.fresh_water,
        'ME Port Hours': readings.equipment[0]?.hours || '',
        'ME Stbd Hours': readings.equipment[1]?.hours || '',
        'Gen Port Hours': readings.equipment[2]?.hours || '',
        'Gen Stbd Hours': readings.equipment[3]?.hours || '',
        'Bilges Dry': readings.misc.bilges_dry === 'OK' ? 'Yes' : 'No',
        'Readings': JSON.stringify(readings), 'Notes': header.notes
      }
      // Baserow rejects '' on single-select and date fields \u2014 omit them entirely
      for (const k of ['Mode', 'Bilges Dry', 'Date']) {
        if (payload[k] === '' || payload[k] === null || payload[k] === undefined) delete payload[k]
      }
      if (editRow) await updateRow('engine_log', editRow.id, payload)
      else await createRow('engine_log', payload)
      await syncEquipmentHours()
      await syncMaintenance()
      setView('table'); load()
    } catch (e) { setError('Failed to save: ' + (e.message || JSON.stringify(e))) }
    finally { setSaving(false) }
  }

  // Engine Room Log -> Equipment hours. Creates missing rows, never lowers hours.
  async function syncEquipmentHours() {
    try {
      const logs = await fetchRows('engine_log')
      const equip = await fetchRows('equipment')
      const plan = planSync(latestReadings(logs || []), equip || [])
      for (const c of plan.creates) {
        await createRow('equipment', {
          'UID': EQUIP_UID[c.name] || c.name,
          'Name': c.name, 'Current Hours': c.hours, 'Status': 'Operational',
          ...(EQUIP_DEFAULTS[c.name] || {})
        })
      }
      for (const u of plan.updates) {
        await updateRow('equipment', u.rowId, { 'Current Hours': u.hours })
      }
      const parts = []
      if (plan.creates.length) parts.push(`Created ${plan.creates.length} equipment row(s)`)
      if (plan.updates.length) parts.push(`Updated hours on ${plan.updates.length}`)
      setHoursMsg(plan.flags.length
        ? { bad: true, text: plan.flags.map(f => f.msg).join(' ') }
        : (parts.length ? { bad: false, text: parts.join(' \u00b7 ') } : null))
    } catch (e) { setHoursMsg({ bad: true, text: 'Equipment hours sync failed: ' + (e.message || 'unknown') }) }
  }

  // ER log -> Maintenance Log (routine + oil) and Maintenance Tasks (service due).
  // Deterministic Log IDs mean a re-saved entry updates its rows instead of duplicating.
  async function syncMaintenance() {
    try {
      const want = maintenanceRowsFor(header, readings)
      if (!want.length) return
      const existing = await fetchRows('mlog')
      const byId = {}
      for (const r of (existing || [])) byId[String(r['Log ID'] || '')] = r
      for (const w of want) {
        const hit = byId[w['Log ID']]
        if (hit) await updateRow('mlog', hit.id, w)
        else await createRow('mlog', w)
      }

      const due = serviceDue(readings)
      if (due.length) {
        const tasks = await fetchRows('mtask')
        const names = new Set((tasks || []).map(t => String(t['Task Name'] || '')))
        for (const d of due) {
          const name = `Service due \u2014 ${d.name} (${d.dueAt}h)`
          if (names.has(name)) continue
          await createRow('mtask', { 'Task Name': name, 'Status': 'Due', 'Next Service Hours': d.dueAt })
        }
        setMaintMsg({ bad: false, text: `Maintenance log updated \u00b7 ${due.length} service(s) now DUE: ` + due.map(d => d.name).join(', ') })
      } else {
        setMaintMsg({ bad: false, text: `Maintenance log updated (${want.length} row(s))` })
      }
    } catch (e) { setMaintMsg({ bad: true, text: 'Maintenance sync failed: ' + (e.message || 'unknown') }) }
  }

  async function handleDelete(row) {
    const label = 'log entry ' + (getVal(row['Date']) || '')
    if (!confirm('Delete the ' + label + '? You can undo it for 10 seconds.')) return
    try { await deleteWithUndo(supabase, { table: 'engine_log', id: row.id, label, onUndone: () => load() }); load() }
    catch (e) { setError('Failed to delete') }
  }

  function buildErlHtml() {
    const NAVY = '#15294a', BLUE = '#2457d6', INK = '#26313c', MUTE = '#5b6878', LINE = '#e4e9f0', SOFT = '#f6f9fc'
    const FONT = "'Trebuchet MS','Segoe UI','Helvetica Neue',Arial,sans-serif"
    const vessel = escHtml((config && config.vessel_name) || 'Vessel')
    const wave = `<span style="display:inline-block;font-size:34px;font-weight:900;color:${BLUE};line-height:1;letter-spacing:-3px;">\u2248</span>`
    const D = v => (v === '' || v === null || v === undefined) ? '\u2014' : escHtml(v)   // every typed reading is escaped (zap-active)
    const r = readings

    const sect = t => `<div style="margin:18px 0 8px;padding-bottom:5px;border-bottom:2px solid ${NAVY};font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:${NAVY};">${t}</div>`
    const wrap = inner => `<div style="border:1px solid ${LINE};border-radius:8px;overflow:hidden;"><table style="width:100%;border-collapse:collapse;font-size:11px;">${inner}</table></div>`
    const th = t => `<th style="background:${NAVY};color:#fff;padding:7px 10px;text-align:left;font-size:9.5px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;">${escHtml(t)}</th>`
    const td = (t, b) => `<td style="border-top:1px solid ${LINE};padding:7px 10px;color:${INK};${b ? 'font-weight:700;' : ''}">${t}</td>`
    const lbl = t => `<td style="border-top:1px solid ${LINE};padding:7px 10px;color:${MUTE};font-weight:700;font-size:10px;background:${SOFT};width:38%;">${escHtml(t)}</td>`

    const pairs = (title, rows) => `${sect(title)}${wrap(rows.map(([k, v]) => `<tr>${lbl(k)}${td(D(v))}</tr>`).join(''))}`
    const twoCol = (title, rows, h1, h2) => `${sect(title)}${wrap(
      `<tr><th style="background:${NAVY};color:#fff;padding:7px 10px;text-align:left;font-size:9.5px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;"></th>${th(h1)}${th(h2)}</tr>` +
      rows.map(([k, a, b]) => `<tr>${lbl(k)}${td(D(a))}${td(D(b))}</tr>`).join('')
    )}`

    const t = r.tanks || {}
    const gp = r.gen_port || {}, gs = r.gen_stbd || {}
    const eq = r.equipment || []
    const misc = r.misc || {}, cf = r.confirm || {}, ac = r.ac || {}
    const bats = r.batteries || [], ros = r.ro_plants || [], sp = r.shore_power || [], bus = r.busbar || []

    const eqRows = eq.map((e, i) => `<tr>${lbl(e.name || '')}${td(D(e.hours))}${td(D(e.next_service))}${td(D(e.oil_added))}</tr>`).join('')
    const eqBlock = `${sect('Equipment Hours')}${wrap(
      `<tr><th style="background:${NAVY};color:#fff;padding:7px 10px;text-align:left;font-size:9.5px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;"></th>${th('Hours')}${th('Next Service')}${th('Oil Added')}</tr>${eqRows}`
    )}`

    const spRows = sp.map(l => `<tr>${lbl('Line ' + l.line)}${td(D(l.voltage))}${td(D(l.current))}${td(D(l.load_kva))}${td(D(l.convertor))}</tr>`).join('')
    const spBlock = `${sect('Shore Power')}${wrap(
      `<tr><th style="background:${NAVY};color:#fff;padding:7px 10px;"></th>${th('Voltage')}${th('Current')}${th('Load kVA')}${th('Convertor %')}</tr>${spRows}`
    )}`

    const busBlock = `${sect('Busbar')}${wrap(
      `<tr><th style="background:${NAVY};color:#fff;padding:7px 10px;"></th>${bus.map(b => th('Bus ' + b.bus)).join('')}</tr>` +
      [['Voltage','voltage'],['Load','load'],['Frequency','frequency'],['Power Factor','power_factor']]
        .map(([label, k]) => `<tr>${lbl(label)}${bus.map(b => td(D(b[k]))).join('')}</tr>`).join('')
    )}`

    const batBlock = `${sect('Batteries')}${wrap(
      `<tr><th style="background:${NAVY};color:#fff;padding:7px 10px;"></th>${th('Voltage')}${th('Current')}</tr>` +
      bats.map(b => `<tr>${lbl(b.name)}${td(D(b.voltage))}${td(D(b.current))}</tr>`).join('')
    )}`

    const roBlock = `${sect('RO Plants')}${wrap(
      `<tr><th style="background:${NAVY};color:#fff;padding:7px 10px;"></th>${ros.map(p => th('Plant ' + p.plant)).join('')}</tr>` +
      [['High Press','high_press'],['Low Press','low_press'],['TDS','tds'],['Production','prod']]
        .map(([label, k]) => `<tr>${lbl(label)}${ros.map(p => td(D(p[k]))).join('')}</tr>`).join('')
    )}`

    return `<div style="width:960px;font-family:${FONT};color:${INK};background:#fff;">
      <style>* { -webkit-print-color-adjust:exact; print-color-adjust:exact; box-sizing:border-box; } td,th{word-wrap:break-word;white-space:normal;}</style>
      <div style="padding:18px 30px 14px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid ${LINE};">
        <div style="display:flex;align-items:center;gap:13px;">${wave}<span style="font-size:22px;font-weight:800;letter-spacing:.13em;color:${NAVY};">HEYCAPTAIN</span></div>
        <div style="font-size:10px;font-weight:700;letter-spacing:.17em;color:${MUTE};text-transform:uppercase;">Safety Management System</div>
      </div>
      <div style="background:${NAVY};padding:22px 30px;display:flex;justify-content:space-between;align-items:center;">
        <div><div style="font-size:26px;font-weight:700;color:#fff;letter-spacing:.02em;">ENGINE ROOM DAY LOG</div><div style="font-size:12.5px;color:#aec3d6;margin-top:4px;">${vessel}</div></div>
        <div style="border:1.5px solid ${BLUE};border-radius:9px;padding:10px 18px;text-align:center;"><div style="font-size:9px;color:#aec3d6;letter-spacing:.14em;margin-bottom:3px;">DATE</div><div style="font-size:15px;font-weight:700;color:#fff;">${D(header.date)}</div></div>
      </div>
      <div style="padding:20px 30px 26px;">
        ${pairs('Entry', [['Location', header.location], ['Mode', header.mode], ['Signed By', header.signed_by]])}
        <div style="display:flex;gap:22px;align-items:flex-start;">
          <div style="flex:1;">
            ${pairs('Tank Levels (US Gal)', [
              ['Fuel Total', t.fuel_total], ['Day Tank', t.day_tank], ['Port Aft', t.port_aft], ['Stbd Aft', t.stbd_aft],
              ['Port Mid', t.port_mid], ['Stbd Mid', t.stbd_mid], ['Port Fwd', t.port_fwd], ['Stbd Fwd', t.stbd_fwd],
              ['Black/Grey', t.black_grey], ['Clean LO', t.clean_lo], ['Waste LO', t.waste_lo], ['Fresh Water', t.fresh_water],
            ])}
            ${pairs('Confirm', [['Main Hyd Level', cf.main_hyd], ['Passarelle Hyd Level', cf.passarelle_hyd], ['Steering Hyd Level', cf.steering_hyd]])}
            ${pairs('Misc.', [['Compressed Air', misc.compressed_air], ['Vacuum System', misc.vacuum_system], ['E/R Bilges Dry', misc.bilges_dry], ['Water Softener', misc.water_softener], ['ER Fans', misc.er_fans]])}
          </div>
          <div style="flex:1;">
            ${twoCol('Generator Readings', [
              ['Hours', gp.hours, gs.hours], ['Coolant Temp \u00b0F', gp.coolant_temp, gs.coolant_temp],
              ['LO Pressure', gp.lo_pressure, gs.lo_pressure], ['LO Temperature', gp.lo_temp, gs.lo_temp],
              ['Power KW', gp.power_kw, gs.power_kw], ['Frequency Hz', gp.frequency, gs.frequency],
              ['Volts', gp.volts, gs.volts], ['RPM', gp.rpm, gs.rpm],
            ], 'Port', 'Stbd')}
            ${eqBlock}
            ${pairs('Air Conditioning', [['Mode', ac.mode], ['Heat', ac.heat], ['Cool', ac.cool], ['Return Temp', ac.return_temp], ['Supply Temp', ac.supply_temp], ['CW Press', ac.cw_press]])}
          </div>
        </div>
        ${spBlock}
        <div style="display:flex;gap:22px;align-items:flex-start;">
          <div style="flex:1;">${busBlock}</div>
          <div style="flex:1;">${roBlock}</div>
        </div>
        ${batBlock}
        ${sect('Notes')}
        <div style="border:1px solid ${LINE};border-radius:8px;padding:12px 14px;font-size:11.5px;line-height:1.6;min-height:52px;color:${INK};">${escHtml(header.notes) || '\u2014'}</div>
        <div style="margin-top:20px;padding-top:11px;border-top:1px solid ${LINE};display:flex;justify-content:space-between;align-items:center;font-size:8.5px;color:${MUTE};">
          <span style="font-weight:800;letter-spacing:.12em;color:${NAVY};">HEYCAPTAIN</span>
          <span style="letter-spacing:.03em;">Hey Captain &middot; Superyacht Safety Management System</span>
        </div>
      </div>
    </div>`
  }

  function handlePrint() {
    const el = document.createElement('div')
    el.innerHTML = buildErlHtml()
    document.body.appendChild(el)
    const fname = ('ER_Day_Log_' + (header.date || 'entry')).replace(/[^a-z0-9]+/gi, '_') + '.pdf'
    const opt = { margin: 8, filename: fname, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true, windowWidth: 960, width: 960 }, pagebreak: { mode: ['legacy'] }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' } }
    html2pdf().set(opt).from(el).save().then(() => document.body.removeChild(el))
  }

  function upd(section, key, val) {
    setReadings(r => ({ ...r, [section]: { ...r[section], [key]: val } }))
  }
  function updArr(section, idx, key, val) {
    setReadings(r => { const arr = [...r[section]]; arr[idx] = { ...arr[idx], [key]: val }; return { ...r, [section]: arr } })
  }

  const modeColor = (m) => {
    if (m === 'Port') return { background: 'rgba(52,181,120,.15)', color: '#137050' }
    if (m === 'At Anchor') return { background: 'rgba(234,179,8,.15)', color: '#8a5c0a' }
    if (m === 'Underway') return { background: 'rgba(106,170,212,.15)', color: '#2a6a96' }
    return {}
  }

  // ── TABLE VIEW ──
  if (view === 'table') return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <button style={btnPrimary} onClick={openNew}>+ New Entry</button>
      </div>
      {error && <div style={{ color: '#b4313c', padding: '8px 16px' }}>{error}</div>}
      {maintMsg && (
        <div style={{
          margin: '8px 16px', padding: '10px 14px', borderRadius: 8, fontSize: 13,
          border: '1px solid ' + (maintMsg.bad ? '#E5484D' : '#D6E0F5'),
          background: maintMsg.bad ? 'rgba(229,72,77,.06)' : 'rgba(17,85,245,.05)',
          color: maintMsg.bad ? '#B4262A' : '#16233B'
        }}>
          {maintMsg.bad ? '\u26a0 ' : ''}{maintMsg.text}
          <button onClick={() => setMaintMsg(null)} style={{
            float: 'right', border: 'none', background: 'transparent',
            cursor: 'pointer', color: 'inherit', fontSize: 14
          }}>&times;</button>
        </div>
      )}
      {hoursMsg && (
        <div style={{
          margin: '8px 16px', padding: '10px 14px', borderRadius: 8, fontSize: 13,
          border: '1px solid ' + (hoursMsg.bad ? '#E5484D' : '#D6E0F5'),
          background: hoursMsg.bad ? 'rgba(229,72,77,.06)' : 'rgba(17,85,245,.05)',
          color: hoursMsg.bad ? '#B4262A' : '#16233B'
        }}>
          {hoursMsg.bad ? '\u26a0 ' : ''}{hoursMsg.text}
          <button onClick={() => setHoursMsg(null)} style={{
            float: 'right', border: 'none', background: 'transparent',
            cursor: 'pointer', color: 'inherit', fontSize: 14
          }}>&times;</button>
        </div>
      )}
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>No entries yet. Click <strong>+ New Entry</strong> to add the first log.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Date', 'Location', 'Mode', 'Signed By', ''].map(h => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={td}>{fmtDate(getVal(row['Date']))}</td>
                  <td style={td}>{getVal(row['Location'])}</td>
                  <td style={td}><span style={{ ...pill, ...modeColor(getVal(row['Mode'])) }}>{getVal(row['Mode'])}</span></td>
                  <td style={td}>{getVal(row['Signed By'])}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={btnSm} onClick={() => openView(row)}>View</button>
                      <button style={{ ...btnSm, color: '#b4313c' }} onClick={() => handleDelete(row)}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )

  // ── FORM / TEMPLATE VIEW ──
  return (
    <>
      {/* Toolbar */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <button style={btnSecondary} onClick={() => setView('table')}>← Back</button>
        <div style={{ display: 'flex', gap: 8 }}>
          {error && <span style={{ color: '#b4313c', fontSize: 12, alignSelf: 'center' }}>{error}</span>}
          <button style={btnSecondary} onClick={handlePrint}>🖨 Print</button>
          <button style={btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Entry'}</button>
        </div>
      </div>

      {/* Template */}
      <div ref={printRef} className="print-area lb-form" style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto', fontSize: 13 }}>

        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', letterSpacing: 2 }}>{config?.vessel_name?.toUpperCase()}</div>
          <div style={{ fontSize: 14, color: 'var(--muted)', marginTop: 4 }}>ENGINE ROOM DAY LOG</div>
        </div>

        {/* Header row */}
        <div className="lb-grid" style={{ ...sectionBox, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
          {[['Date', 'date', 'date'], ['Location', 'location', 'text'], ['Mode', 'mode', 'select'], ['Signed By', 'signed_by', 'text']].map(([label, key, type]) => (
            <div key={key}>
              <div style={fieldLabel}>{label}</div>
              {type === 'select' ? (
                <select style={fieldInput} value={header[key]} onChange={e => setHeader({ ...header, [key]: e.target.value })}>
                  <option value=''>Select...</option>
                  {['Port', 'At Anchor', 'Underway'].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : type === 'date' ? (
                <DateField
                  value={header[key]}
                  onChange={v => setHeader({ ...header, [key]: v })}
                  style={fieldInput}
                />
              ) : (
                <input style={fieldInput} type={type} value={header[key]} onChange={e => setHeader({ ...header, [key]: e.target.value })} />
              )}
            </div>
          ))}
        </div>

        <div className="lb-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

          {/* TANK LEVELS */}
          <div style={sectionBox}>
            <div style={sectionTitle}>TANK LEVELS</div>
            <table style={innerTable}>
              <tbody>
                {[['fuel_total','Fuel Total'],['day_tank','Day Tank'],['port_aft','Port Aft'],['stbd_aft','Stbd Aft'],['port_mid','Port Mid'],['stbd_mid','Stbd Mid'],['port_fwd','Port Fwd'],['stbd_fwd','Stbd Fwd'],['black_grey','Black/Grey'],['clean_lo','Clean LO'],['waste_lo','Waste LO'],['fresh_water','Fresh Water']].map(([k,label]) => (
                  <tr key={k}>
                    <td style={cellLabel}>{label}</td>
                    <td><input style={cellInput} type="text" placeholder="e.g. 800 L" value={readings.tanks[k] || ''} onChange={e => upd('tanks', k, e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>CONFIRM</div>
            {[['main_hyd','Main Hyd Level'],['passarelle_hyd','Passarelle Hyd Level'],['steering_hyd','Steering Hyd Level']].map(([k, label]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</span>
                <select style={{ ...cellInput, width: 80 }} value={readings.confirm[k]} onChange={e => upd('confirm', k, e.target.value)}>
                  <option>OK</option><option>Not OK</option>
                </select>
              </div>
            ))}
          </div>

          {/* RIGHT COLUMN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* GENERATOR READINGS */}
            <div style={sectionBox}>
              <div style={sectionTitle}>GENERATOR READINGS</div>
              <table style={innerTable}>
                <thead>
                  <tr>
                    <td style={cellLabel}></td>
                    <td style={{ ...cellLabel, textAlign: 'center', fontWeight: 600 }}>Port</td>
                    <td style={{ ...cellLabel, textAlign: 'center', fontWeight: 600 }}>Stbd</td>
                  </tr>
                </thead>
                <tbody>
                  {[['hours','Hours'],['coolant_temp','Coolant Temp °F'],['lo_pressure','LO Pressure'],['lo_temp','LO Temperature'],['power_kw','Power KW'],['frequency','Frequency Hz'],['volts','Volts'],['rpm','RPM']].map(([k, label]) => (
                    <tr key={k}>
                      <td style={cellLabel}>{label}</td>
                      <td><input style={cellInput} type="number" value={readings.gen_port[k] || ''} onChange={e => upd('gen_port', k, e.target.value)} /></td>
                      <td><input style={cellInput} type="number" value={readings.gen_stbd[k] || ''} onChange={e => upd('gen_stbd', k, e.target.value)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* EQUIPMENT HOURS */}
            <div style={sectionBox}>
              <div style={sectionTitle}>EQUIPMENT HOURS</div>
              <table style={innerTable}>
                <thead>
                  <tr>
                    <td style={cellLabel}></td>
                    <td style={{ ...cellLabel, fontWeight: 600 }}>Hours</td>
                    <td style={{ ...cellLabel, fontWeight: 600 }}>Next Service</td>
                    <td style={{ ...cellLabel, fontWeight: 600 }}>Oil Added</td>
                  </tr>
                </thead>
                <tbody>
                  {readings.equipment.map((eq, i) => (
                    <tr key={i}>
                      <td style={cellLabel}>{eq.name}</td>
                      <td><input style={cellInput} type="number" value={eq.hours || ''} onChange={e => updArr('equipment', i, 'hours', e.target.value)} /></td>
                      <td><input style={cellInput} type="number" value={eq.next_service || ''} onChange={e => updArr('equipment', i, 'next_service', e.target.value)} /></td>
                      <td><input style={cellInput} value={eq.oil_added || ''} onChange={e => updArr('equipment', i, 'oil_added', e.target.value)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* MISC */}
            <div style={sectionBox}>
              <div style={sectionTitle}>MISC.</div>
              <table style={innerTable}>
                <tbody>
                  {[['compressed_air','Compressed Air'],['vacuum_system','Vacuum System'],['bilges_dry','E/R Bilges Dry'],['water_softener','Water Softener'],['er_fans','ER Fans']].map(([k, label]) => (
                    <tr key={k}>
                      <td style={cellLabel}>{label}</td>
                      <td>
                        <select style={{ ...cellInput, width: 80 }} value={readings.misc[k]} onChange={e => upd('misc', k, e.target.value)}>
                          <option>OK</option><option>Not OK</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* SHORE POWER */}
        <div style={{ ...sectionBox, marginBottom: 16 }}>
          <div style={sectionTitle}>SHORE POWER READINGS</div>
          <table style={{ ...innerTable, width: '100%' }}>
            <thead>
              <tr>
                <td style={cellLabel}>LINE</td>
                <td style={{ ...cellLabel, textAlign: 'center' }} colSpan={2}>VOLTAGE V</td>
                <td style={{ ...cellLabel, textAlign: 'center' }} colSpan={2}>CURRENT A</td>
                <td style={{ ...cellLabel, textAlign: 'center' }} colSpan={2}>LOAD KVA</td>
                <td style={cellLabel}>CONVERTOR %</td>
              </tr>
              <tr>
                <td style={cellLabel}></td>
                {['P','S','P','S','P','S'].map((l,i) => <td key={i} style={{ ...cellLabel, textAlign: 'center' }}>{l}</td>)}
                <td></td>
              </tr>
            </thead>
            <tbody>
              {readings.shore_power.map((sp, i) => (
                <tr key={i}>
                  <td style={cellLabel}>{sp.line}</td>
                  {[['voltage_p'],['voltage_s'],['current_p'],['current_s'],['load_kva_p'],['load_kva_s'],['convertor']].map(([k]) => (
                    <td key={k}><input style={cellInput} type="number" value={sp[k] || ''} onChange={e => updArr('shore_power', i, k, e.target.value)} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* BUSBAR + BATTERIES side by side */}
        <div className="lb-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div style={sectionBox}>
            <div style={sectionTitle}>BUSBAR</div>
            <table style={innerTable}>
              <thead>
                <tr>
                  <td style={cellLabel}></td>
                  {['1','2','3'].map(b => <td key={b} style={{ ...cellLabel, textAlign: 'center', fontWeight: 600 }}>{b}</td>)}
                </tr>
              </thead>
              <tbody>
                {[['voltage','Voltage'],['load','Load'],['frequency','Frequency'],['power_factor','Power Factor']].map(([k, label]) => (
                  <tr key={k}>
                    <td style={cellLabel}>{label}</td>
                    {readings.busbar.map((b, i) => (
                      <td key={i}><input style={cellInput} type="number" value={b[k] || ''} onChange={e => updArr('busbar', i, k, e.target.value)} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={sectionBox}>
            <div style={sectionTitle}>BATTERIES</div>
            <table style={innerTable}>
              <thead>
                <tr>
                  <td style={cellLabel}></td>
                  <td style={{ ...cellLabel, fontWeight: 600 }}>Voltage</td>
                  <td style={{ ...cellLabel, fontWeight: 600 }}>Current</td>
                </tr>
              </thead>
              <tbody>
                {readings.batteries.map((b, i) => (
                  <tr key={i}>
                    <td style={cellLabel}>{b.name}</td>
                    <td><input style={cellInput} type="number" value={b.voltage || ''} onChange={e => updArr('batteries', i, 'voltage', e.target.value)} /></td>
                    <td><input style={cellInput} type="number" value={b.current || ''} onChange={e => updArr('batteries', i, 'current', e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* RO PLANTS + AC side by side */}
        <div className="lb-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div style={sectionBox}>
            <div style={sectionTitle}>RO PLANTS</div>
            <table style={innerTable}>
              <thead>
                <tr>
                  <td style={cellLabel}></td>
                  <td style={{ ...cellLabel, fontWeight: 600 }}>Plant 1</td>
                  <td style={{ ...cellLabel, fontWeight: 600 }}>Plant 2</td>
                </tr>
              </thead>
              <tbody>
                {[['high_press','High Press'],['low_press','Low Press'],['tds','TDS'],['prod','Production']].map(([k, label]) => (
                  <tr key={k}>
                    <td style={cellLabel}>{label}</td>
                    {readings.ro_plants.map((r, i) => (
                      <td key={i}><input style={cellInput} type="number" value={r[k] || ''} onChange={e => updArr('ro_plants', i, k, e.target.value)} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={sectionBox}>
            <div style={sectionTitle}>AIR CONDITIONING</div>
            <table style={innerTable}>
              <tbody>
                {[['mode','Mode','text'],['heat','Heat','number'],['cool','Cool X','number'],['return_temp','Return Temp','number'],['supply_temp','Supply Temp','number'],['cw_press','CW Press','number']].map(([k, label, type]) => (
                  <tr key={k}>
                    <td style={cellLabel}>{label}</td>
                    <td><input style={cellInput} type={type} value={readings.ac[k] || ''} onChange={e => upd('ac', k, e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes */}
        <div style={{ ...sectionBox, marginBottom: 16 }}>
          <div style={sectionTitle}>NOTES</div>
          <textarea style={{ ...fieldInput, width: '100%', height: 80, resize: 'vertical', boxSizing: 'border-box' }}
            value={header.notes} onChange={e => setHeader({ ...header, notes: e.target.value })} />
        </div>

      </div>
    </>
  )
}

const th = { padding: '8px 12px', textAlign: 'left', fontSize: 11, color: 'var(--muted)', fontWeight: 600 }
const td = { padding: '8px 12px', color: 'var(--text)', verticalAlign: 'middle' }
const pill = { display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }
const btnSm = { padding: '3px 8px', fontSize: 11, borderRadius: 4, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', cursor: 'pointer' }
const btnPrimary = { padding: '8px 18px', borderRadius: 6, border: 'none', background: 'var(--gold)', color: '#000', fontWeight: 600, cursor: 'pointer', fontSize: 13 }
const btnSecondary = { padding: '8px 18px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', cursor: 'pointer', fontSize: 13 }
const sectionBox = { border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: 'var(--surface)' }
const sectionTitle = { fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: 1, marginBottom: 8, textTransform: 'uppercase' }
const innerTable = { width: '100%', borderCollapse: 'collapse', fontSize: 12 }
const cellLabel = { padding: '3px 8px 3px 0', color: 'var(--muted)', whiteSpace: 'nowrap', fontSize: 12, verticalAlign: 'middle' }
const cellInput = { padding: '3px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 12, width: '100%', boxSizing: 'border-box' }
const fieldLabel = { fontSize: 11, color: 'var(--muted)', marginBottom: 4 }
const fieldInput = { padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, width: '100%', boxSizing: 'border-box' }
